// src/pages/Signup.jsx
// git commit: "feat(Signup): log user in immediately after signup — no email verification required"
import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../api/api";
import { toast } from "react-toastify";
import { ArrowLeft, Moon, Sun, Eye, EyeOff } from "lucide-react";

// Retry helper — retries on network/5xx errors (common on slow mobile and
// during brief shared-hosting restart gaps), NOT on 4xx validation errors.
async function postWithRetry(url, data, maxRetries = 3) {
  let lastError;
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const res = await api.post(url, data);
      return res;
    } catch (err) {
      lastError = err;
      const status = err.response?.status;
      const shouldRetry =
        !err.response || // network error (no response at all)
        status >= 500; // server error

      if (!shouldRetry || attempt === maxRetries) {
        throw err;
      }

      await new Promise((r) => setTimeout(r, 1200 * attempt));
      console.log(`[Signup] Retrying attempt ${attempt + 1}...`);
    }
  }
  throw lastError;
}

export default function SignupPage() {
  const [searchParams] = useSearchParams();
  const referralCodeFromUrl = (searchParams.get("ref") || "").trim().toUpperCase();

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });

  // Prefilled from the link if present, but always editable — lets a user
  // type a code told to them verbally, or paste/edit one from a link.
  const [referralCodeInput, setReferralCodeInput] = useState(referralCodeFromUrl);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [darkMode, setDarkMode] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem("theme");
    const prefersDark = window.matchMedia(
      "(prefers-color-scheme: dark)"
    ).matches;
    const enabled = saved ? saved === "dark" : prefersDark;
    setDarkMode(enabled);
    document.documentElement.classList.toggle("dark", enabled);
  }, []);

  const toggleTheme = () => {
    const next = !darkMode;
    setDarkMode(next);
    document.documentElement.classList.toggle("dark", next);
    localStorage.setItem("theme", next ? "dark" : "light");
  };

  const navigate = useNavigate();

  const set = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");

    // Client-side validation
    const { firstName, lastName, username, email, phone, password, confirmPassword } = form;

    if (!firstName.trim() || !lastName.trim()) {
      setMessage("First and last name are required");
      return;
    }
    if (!username.trim()) {
      setMessage("Username is required");
      return;
    }
    if (!email.trim()) {
      setMessage("Email address is required");
      return;
    }
    if (password.length < 6) {
      setMessage("Password must be at least 6 characters");
      return;
    }
    if (password !== confirmPassword) {
      setMessage("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      const cleanEmail = email.trim().toLowerCase();

      await postWithRetry("/user/signup", {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        // fullName derived server-side — we also send it as a hint
        fullName: `${firstName.trim()} ${lastName.trim()}`,
        username: username.trim().toLowerCase(),
        email: cleanEmail,
        phone: phone.trim(),
        password,
        referralCode: referralCodeInput.trim() || undefined,
      });

      // Account created but not yet verified — no tokens issued.
      // Passed as a query param (not navigation state) so it survives a
      // page refresh and matches what CheckEmail.jsx reads via useSearchParams.
      toast.success("Account created! Check your email to verify.");
      navigate(`/check-email?email=${encodeURIComponent(cleanEmail)}`);
    } catch (err) {
      const status = err.response?.status;
      if (status === 400) {
        setMessage(err.response.data?.message || "Please check your details and try again.");
      } else if (status === 429) {
        setMessage("Too many attempts. Please wait a few minutes and try again.");
      } else if (status === 503) {
        setMessage("Server is still starting up. Please try again in a few seconds.");
      } else if (!err.response) {
        setMessage("Connection error. Please check your internet and try again.");
      } else {
        setMessage("Registration failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full h-[42px] px-3 text-sm border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-purple-500 disabled:opacity-60";
  const labelClass =
    "block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1";

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="w-full border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="text-gray-600 dark:text-gray-300 hover:text-purple-600 transition"
              type="button"
            >
              <ArrowLeft size={18} />
            </button>
            <Link to="/" className="flex items-center">
              <img
                src="/assets/icons/logo3.png"
                alt="LARRYDIGITALS"
                className="h-5 sm:h-10 md:h-12 lg:h-14 w-auto object-contain drop-shadow-md dark:invert dark:hue-rotate-180"
              />
            </Link>
          </div>
          <button
            type="button"
            onClick={toggleTheme}
            className="text-gray-600 dark:text-gray-300 hover:text-purple-600 transition"
          >
            {darkMode ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        </div>
      </header>

      {/* Form */}
      <main className="flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-[420px] bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm p-6">
          <div className="text-center mb-6">
            <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Create Account
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Sign up to get started with LARRYDIGITALS
            </p>
          </div>

          {referralCodeFromUrl && (
            <div className="mb-4 px-3 py-2.5 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg text-xs text-purple-700 dark:text-purple-300 text-center font-medium">
              🎉 You were invited with code <span className="font-bold">{referralCodeFromUrl}</span>
            </div>
          )}

          {message && (
            <div className="mb-4 px-3 py-2.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-600 dark:text-red-400 text-center">
              {message}
            </div>
          )}

          <form
            className="space-y-4"
            onSubmit={handleSubmit}
            autoComplete="on"
            noValidate
          >
            {/* First Name */}
            <div>
              <label className={labelClass}>First Name *</label>
              <input
                type="text"
                placeholder="John"
                value={form.firstName}
                onChange={set("firstName")}
                disabled={loading}
                required
                autoComplete="given-name"
                autoCapitalize="words"
                className={inputClass}
              />
            </div>

            {/* Last Name */}
            <div>
              <label className={labelClass}>Last Name *</label>
              <input
                type="text"
                placeholder="Doe"
                value={form.lastName}
                onChange={set("lastName")}
                disabled={loading}
                required
                autoComplete="family-name"
                autoCapitalize="words"
                className={inputClass}
              />
            </div>

            {/* Username */}
            <div>
              <label className={labelClass}>Username *</label>
              <input
                type="text"
                placeholder="johndoe"
                value={form.username}
                onChange={set("username")}
                disabled={loading}
                required
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck="false"
                className={inputClass}
              />
            </div>

            {/* Email */}
            <div>
              <label className={labelClass}>Email Address *</label>
              <input
                type="email"
                placeholder="you@example.com"
                value={form.email}
                onChange={set("email")}
                disabled={loading}
                required
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck="false"
                inputMode="email"
                className={inputClass}
              />
            </div>

            {/* Phone */}
            <div>
              <label className={labelClass}>Phone Number *</label>
              <input
                type="tel"
                placeholder="08123456789"
                value={form.phone}
                onChange={set("phone")}
                disabled={loading}
                required
                autoComplete="tel"
                inputMode="tel"
                maxLength={11}
                className={inputClass}
              />
            </div>

            {/* Referral code — optional, prefilled from link but editable */}
            <div>
              <label className={labelClass}>Referral Code (optional)</label>
              <input
                type="text"
                placeholder="e.g. JOHN4F2A"
                value={referralCodeInput}
                onChange={(e) => setReferralCodeInput(e.target.value.toUpperCase())}
                disabled={loading}
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck="false"
                className={inputClass}
              />
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                Have a friend's code? Enter it here — they'll earn a bonus once you make your first purchase.
              </p>
            </div>

            {/* Password */}
            <div>
              <label className={labelClass}>Password *</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Min. 6 characters"
                  value={form.password}
                  onChange={set("password")}
                  disabled={loading}
                  required
                  autoComplete="new-password"
                  className={`${inputClass} pr-10`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label className={labelClass}>Confirm Password *</label>
              <div className="relative">
                <input
                  type={showConfirm ? "text" : "password"}
                  placeholder="Repeat your password"
                  value={form.confirmPassword}
                  onChange={set("confirmPassword")}
                  disabled={loading}
                  required
                  autoComplete="new-password"
                  className={`${inputClass} pr-10`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showConfirm ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              {/* Live password match indicator */}
              {form.confirmPassword && (
                <p
                  className={`text-xs mt-1 ${
                    form.password === form.confirmPassword
                      ? "text-green-600 dark:text-green-400"
                      : "text-red-500"
                  }`}
                >
                  {form.password === form.confirmPassword
                    ? "✓ Passwords match"
                    : "✗ Passwords do not match"}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full h-[42px] text-sm font-medium rounded-md transition ${
                loading
                  ? "bg-purple-300 cursor-not-allowed"
                  : "bg-purple-600 hover:bg-purple-700 active:bg-purple-800"
              } text-white`}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  Creating account...
                </span>
              ) : (
                "Create Account"
              )}
            </button>
          </form>

          <p className="mt-6 text-center text-xs text-gray-600 dark:text-gray-400">
            Already have an account?{" "}
            <Link
              to="/login"
              className="text-purple-600 dark:text-purple-400 font-medium hover:underline"
            >
              Sign In
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
}